import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { sourcesAPI, queueAPI } from '../api/client'
import { useToast } from '../components/Toast'
import { Globe, Play, Plus, Trash2, Settings } from 'lucide-react'

function SourceCard({ source, onParse, onDelete }) {
  const lastParsed = source.last_parsed_at
    ? new Date(source.last_parsed_at).toLocaleString()
    : 'Никогда'

  return (
    <div className="glass p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
            <Globe className="text-primary" size={24} />
          </div>
          <div>
            <h3 className="font-semibold">{source.name}</h3>
            <p className="text-sm opacity-50">{source.domain}</p>
          </div>
        </div>
        <span className={`badge ${source.is_active ? 'badge-approved' : 'badge-rejected'}`}>
          {source.is_active ? 'Активен' : 'Выключен'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm opacity-50">Авторы</p>
          <p className="font-semibold">{source.authors_count || 0}</p>
        </div>
        <div>
          <p className="text-sm opacity-50">Изображения</p>
          <p className="font-semibold">{source.images_count || 0}</p>
        </div>
        <div>
          <p className="text-sm opacity-50">Парсер</p>
          <p className="font-semibold">{source.parser_type}</p>
        </div>
        <div>
          <p className="text-sm opacity-50">Последний запуск</p>
          <p className="font-semibold text-xs">{lastParsed}</p>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => onParse(source)}
          className="btn-primary flex-1 flex items-center justify-center gap-2"
        >
          <Play size={18} />
          Запустить парсинг
        </button>
        <button
          onClick={() => onDelete(source.id)}
          className="btn-ghost px-3"
        >
          <Trash2 size={18} />
        </button>
      </div>
    </div>
  )
}

function AddSourceModal({ isOpen, onClose, onSubmit }) {
  const [name, setName] = useState('')
  const [domain, setDomain] = useState('')
  const [parserType, setParserType] = useState('playwright')

  if (!isOpen) return null

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit({ name, domain, parser_type: parserType })
    setName('')
    setDomain('')
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="glass p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h2 className="text-xl font-bold mb-4">Добавить источник</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm opacity-60 mb-1">Название</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/10 focus:border-primary focus:outline-none"
              placeholder="3D Galleru"
              required
            />
          </div>
          <div>
            <label className="block text-sm opacity-60 mb-1">Домен</label>
            <input
              type="text"
              value={domain}
              onChange={e => setDomain(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/10 focus:border-primary focus:outline-none"
              placeholder="3d-galleru.ru"
              required
            />
          </div>
          <div>
            <label className="block text-sm opacity-60 mb-1">Тип парсера</label>
            <select
              value={parserType}
              onChange={e => setParserType(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/10 focus:border-primary focus:outline-none"
            >
              <option value="playwright">Playwright</option>
              <option value="requests">Requests</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1">
              Отмена
            </button>
            <button type="submit" className="btn-primary flex-1">
              Добавить
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function Sources() {
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const [showAddModal, setShowAddModal] = useState(false)

  const { data: sources, isLoading } = useQuery('sources', () => sourcesAPI.getAll())

  const createMutation = useMutation(sourcesAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('sources')
      addToast('Источник успешно создан')
    },
    onError: (err) => {
      addToast(err.response?.data?.detail || 'Не удалось создать источник', 'error')
    }
  })

  const deleteMutation = useMutation(sourcesAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('sources')
      addToast('Источник удалён')
    },
    onError: (err) => {
      addToast(err.response?.data?.detail || 'Не удалось удалить источник', 'error')
    }
  })

  const parseMutation = useMutation(
    (source) => queueAPI.createTask({
      task_type: 'parse',
      payload: {
        source: source.domain,
        source_id: source.id,
        max_pages: 5
      },
      priority: 1
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('queueStatus')
        queryClient.invalidateQueries('tasks')
        addToast('Задача парсинга добавлена в очередь')
      },
      onError: (err) => {
        addToast(err.response?.data?.detail || 'Не удалось добавить задачу', 'error')
      }
    }
  )

  const handleParse = (source) => {
    parseMutation.mutate(source)
  }

  const handleDelete = (id) => {
    if (confirm('Удалить этот источник?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleAddSource = (data) => {
    createMutation.mutate(data)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Источники</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} />
          Добавить источник
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-12 opacity-50">Загрузка...</div>
      ) : sources?.length === 0 ? (
        <div className="text-center py-12">
          <Globe size={48} className="mx-auto mb-4 opacity-20" />
          <p className="opacity-50">Источников пока нет</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary mt-4"
          >
            Добавить первый источник
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sources?.map(source => (
            <SourceCard
              key={source.id}
              source={source}
              onParse={handleParse}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <AddSourceModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddSource}
      />
    </div>
  )
}
